import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    Skeleton,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Autocomplete
} from "@mui/material";
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import productService from "../../../../services/productService";
import { FaSearch } from "react-icons/fa";
import { getMarketPlaces } from "../../../../redux/actions/marketplacesActions";
import { getEcommercePlaces } from "../../../../redux/actions/ecommercePlaceActions";
import { CalendarIcon } from "@mui/x-date-pickers";

const ProductTransfers = () => {
    const userId = useSelector((state) => state?.auth?.user?.id);
    const dispatch = useDispatch();

    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [marketplaces, setMarketplaces] = useState([]);
    const [ecommerces, setEcommerces] = useState([]);

    // Filtre state'leri
    const [productNameFilter, setProductNameFilter] = useState("");
    const [marketplaceFilter, setMarketplaceFilter] = useState(null);
    const [ecommerceFilter, setEcommerceFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [dateFromFilter, setDateFromFilter] = useState("");
    const [dateToFilter, setDateToFilter] = useState("");

   const statusConfig = {
        2: { icon: ScheduleIcon, color: "warning", label: "Kontrol ediliyor" },
        1: { icon: CheckCircleIcon, color: "success", label: "Başarılı" },
        3: { icon: CheckCircleIcon, color: "success", label: "Güncellendi" },
        9: { icon: ErrorIcon, color: "error", label: "Hata" }
    };

    const statusOptions = [
        { value: 1, label: "Başarılı" },
        { value: 3, label: "Güncellendi" },
        { value: 9, label: "Hata" }
    ];

    const getProductTransfer = async () => {
        try {
            setLoading(true);
            const response = await productService.getAllProductTransfer({
                userId: userId,
                page: page + 1,
                pageSize: rowsPerPage,
                search: searchTerm,
                productName: productNameFilter,
                marketPlaceId: marketplaceFilter?.value,
                ecommercePlaceId: ecommerceFilter?.value,
                status: statusFilter?.value,
                startDate: dateFromFilter,
                endDate: dateToFilter
            });
            if (response?.data?.data?.data) {
                setTransfers(response.data.data.data);
                setTotalCount(response.data.data.recordsTotal);
            }
        } catch (error) {
            console.error("error", error);
        } finally {
            setLoading(false);
        }
    };

    // Marketplace ve ecommerce verilerini yükle
    useEffect(() => {
        const loadMarketplaces = async () => {
            try {
                // Marketyerlerini yükle
                const marketResponse = await dispatch(getMarketPlaces());
                if (marketResponse.success) {
                    const data = marketResponse?.data.map((x) => ({
                        value: x.id,
                        label: x.name,
                    }));
                    setMarketplaces(data);
                }

                // E-ticaret platformlarını yükle
                const ecommerceResponse = await dispatch(getEcommercePlaces());
                if (ecommerceResponse.success) {
                    const data = ecommerceResponse?.data.map((x) => ({
                        value: x.id,
                        label: x.name,
                    }));
                    setEcommerces(data);
                }
            } catch (error) {
                console.error("Marketplace/Ecommerce yüklenirken hata:", error);
            }
        };

        loadMarketplaces();
    }, [dispatch]);

    useEffect(() => {
        if (userId) {
            getProductTransfer();
        }
    }, [userId, page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        getProductTransfer();
    };

    const handleRefresh = async () => {
        // State'leri temizle
        setProductNameFilter("");
        setMarketplaceFilter(null);
        setEcommerceFilter(null);
        setStatusFilter(null);
        setDateFromFilter("");
        setDateToFilter("");
        setSearchTerm("");
        setPage(0);

        try {
            setLoading(true);
            const response = await productService.getAllProductTransfer({
                userId: userId,
                page: 1,
                pageSize: rowsPerPage,
                search: "",
                productName: "",
                marketPlaceId: "",
                ecommercePlaceId: "",
                status: "",
                startDate: "",
                endDate: ""
            });
            if (response?.data?.data?.data) {
                setTransfers(response.data.data.data);
                setTotalCount(response.data.data.recordsTotal);
            }
        } catch (error) {
            console.error("error", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === "0001-01-01T00:00:00") return "Belirtilmemiş";
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusDisplay = (statusId) => {
        const status = statusConfig[statusId];
        if (!status) return statusId;

        const IconComponent = status.icon;
        return (
            <Chip
                icon={<IconComponent />}
                label={status.label}
                color={status.color}
                size="small"
            />
        );
    };


    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Ürün Transferleri
                </Typography>
                <Grid container spacing={3}>
                    {[...Array(6)].map((_, index) => (
                        <Grid item xs={12} key={index}>
                            <Card>
                                <CardContent>
                                    <Skeleton variant="text" height={40} />
                                    <Skeleton variant="text" height={20} />
                                    <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header ve Filtreler */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4">
                        Ürün Transferleri
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                        sx={{ borderRadius: '8px' }}
                    >
                        Yenile
                    </Button>
                </Box>



                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: 2, sm: 3 },
                        marginBottom: { xs: 2, sm: 4 },
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        backgroundColor: "#F7F9FC",
                        opacity: 1,
                        transition: "opacity 0.2s ease",
                    }}
                >
                    <Box sx={{ marginBottom: { xs: 2, sm: 3 } }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: "#1F1B45",
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                            }}
                        >
                            Filtreler
                        </Typography>
                    </Box>
                    
                    <Grid container spacing={2}>
                        {/* İlk satır - Filtreler */}
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TextField
                                label="Ürün Adı"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={productNameFilter}
                                onChange={(e) => setProductNameFilter(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Autocomplete
                                options={marketplaces}
                                getOptionLabel={(option) => option.label}
                                value={marketplaceFilter}
                                onChange={(event, newValue) => setMarketplaceFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Pazaryeri Platformları"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Autocomplete
                                options={ecommerces}
                                getOptionLabel={(option) => option.label}
                                value={ecommerceFilter}
                                onChange={(event, newValue) => setEcommerceFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="E-Ticaret Platformları"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <Autocomplete
                                options={statusOptions}
                                getOptionLabel={(option) => option.label}
                                value={statusFilter}
                                onChange={(event, newValue) => setStatusFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Durum"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={4}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="Başlangıç Tarihi"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dateFromFilter}
                                    onChange={(e) => setDateFromFilter(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: '8px 12px',
                                        }
                                    }}
                                />
                                <Typography sx={{ color: '#666', fontSize: 20 }}>-</Typography>
                                <TextField
                                    label="Bitiş Tarihi"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dateToFilter}
                                    onChange={(e) => setDateToFilter(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: '8px 12px',
                                        }
                                    }}
                                />
                            </Stack>
                        </Grid>

                        {/* İkinci satır - Butonlar */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 3
                                    }}
                                >
                                    <FaSearch />
                                    Ara
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: '8px',
                                        px: 3
                                    }}
                                >
                                    Temizle
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

            </Box>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="product transfers table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Ürün</TableCell>
                            <TableCell align="center">Pazaryeri</TableCell>
                            <TableCell align="center">Durum</TableCell>
                            <TableCell align="center">Detay</TableCell>
                            <TableCell align="center">Tarih</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transfers.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={row.product.productImages[0]?.imageUrl}
                                            alt={row.product.title}
                                            style={{
                                                width: 50,
                                                height: 50,
                                                marginRight: 12,
                                                objectFit: 'cover',
                                                borderRadius: 4
                                            }}
                                        />
                                        <Box>
                                            <Typography variant="body1" fontWeight="medium">
                                                {row.product.title}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Stok kodu: {row.product.stockCode}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <img
                                        src={row?.ecommercePlace?.iconLink || row?.marketPlace?.iconLink}
                                        alt="Pazaryeri"
                                        style={{ width: 40, height: 40 }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {getStatusDisplay(row.status)}
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {row.userDescription || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {formatDate(row.createDate)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Sayfa başına satır:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
                    }
                />
            </TableContainer>
        </Box>
    );
};

export default ProductTransfers;